import {Component, DestroyRef, inject, OnDestroy, OnInit} from '@angular/core';
import {BaseService} from '../../base/services/base.service';
import {ApexAxisChartSeries, ApexChart, ApexXAxis, ApexYAxis,} from 'ng-apexcharts';
import {FormBuilder, FormGroup} from '@angular/forms';
import {DashboardService} from '../service/dashboard.service';
import {ButtonFilterConfigDTO, FilterDashboardDTO,} from '../dto/dashboard.dto';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {FilterDateKey, GreetingDashboardText,} from '../constant/dashboard.constant';
import {firstValueFrom, Observable} from 'rxjs';
import {
  DataRatedMenus,
  DataReviewedMenus,
  DataVenueOverall,
  DialogReportCategory,
} from '../repository/entity/dashboard.entity';
import {MatDialog} from '@angular/material/dialog';
import {DialogFilterComponent} from './dialog-filter/dialog-filter.component';
import {BranchService} from "../../branch/branch.service";

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss',
})
export class DashboardComponent implements OnInit, OnDestroy {
  public destroyRef = inject(DestroyRef);
  public chart: ApexChart;
  public chartYAxis: ApexYAxis;
  public chartSeries: ApexAxisChartSeries;
  public chartXAxis: ApexXAxis;

  public dateRange: FormGroup;
  public activeQuickFilterBtn: string;
  public buttonConfigs: ButtonFilterConfigDTO[];
  public ratedMenus$: Observable<DataRatedMenus[]>;
  public reviewedMenus$: Observable<DataReviewedMenus[]>;
  public venueOverall$: Observable<DataVenueOverall>;
  public venueSection$: Observable<DataVenueOverall[]>;
  public filterDate$: Observable<FilterDashboardDTO>;

  constructor(
    private baseService: BaseService,
    private fb: FormBuilder,
    private dashboardService: DashboardService,
    private branchService: BranchService,
    private dialog: MatDialog
  ) {
  }

  async ngOnInit() {
    await this.initConfig();
    this.ratedMenus$ = this.dashboardService.ratedMenus;
    this.reviewedMenus$ = this.dashboardService.reviewedMenus;
    this.venueOverall$ = this.dashboardService.venueOverall;
    this.venueSection$ = this.dashboardService.venueSection;
    this.filterDate$ = this.dashboardService.filterDate;
  }

  async initConfig() {
    this.baseService.setSupportingText(GreetingDashboardText);
    this.buttonConfigs = this.dashboardService.buttonConfig;
    this.dashboardService.initChart();
    this.getChart();
    this.fetchData();
    await this.quickFilterDate(FilterDateKey.year);

  }

  fetchData() {
    this.dashboardService
      .fetchDataChart()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();

    this.dashboardService
      .fetchDataOverall()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();

    this.dashboardService
      .fetchDataVenueSection()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();

    this.dashboardService
      .fetchDataRatedMenus()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();

    this.dashboardService
      .fetchDataReviewedMenus()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();

    this.dashboardService
      .fetchListMenu()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  getChart() {
    this.chart = this.dashboardService.chart;
    this.chartYAxis = this.dashboardService.chartYAxis;
    this.dashboardService.chartSeries
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => (this.chartSeries = data),
      });

    this.dashboardService.chartXAxis
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => (this.chartXAxis = data),
      });
  }

  applyDate() {
    const prevDate = this.dashboardService.convertDate(
      this.dateRange.value.start
    );
    const currentDate = this.dashboardService.convertDate(
      this.dateRange.value.end
    );
    this.activeQuickFilterBtn = 'filterButton';
    this.dashboardService.updateDateFilterDate(currentDate, prevDate);
  }

  setDate(start: string | null, end: string | null) {
    this.dateRange = this.fb.group({
      start: [start],
      end: [end],
    });
  }

  async quickFilterDate(key: string) {
    if (this.activeQuickFilterBtn !== key) {
      this.activeQuickFilterBtn = key;
      this.setDate(null, null);
      const branchCodes = await this.branchService.getJoinFilteredBranchCodes();
      this.dashboardService.convertQuickFilterDate(this.activeQuickFilterBtn, branchCodes);
    }
  }

  async onClickFilter() {
    const dialogRef = this.dialog.open(DialogFilterComponent, {
      data: {
        title: 'Filter Dashboard',
      },
      width: '80vh',
      height: '35em',
      disableClose: true
    });

    const dialogResult: DialogReportCategory | null = await firstValueFrom(dialogRef.afterClosed());

    if (dialogResult) {
      const {reportCategory, branch, menuIds} = dialogResult;
      this.dashboardService.setFilterCategory(reportCategory, branch, menuIds);
    }
  }

  getOverallLabel(category: string | undefined) {
    return category === "menu" ? "Menu Rating" : "Restaurant Rating";
  }

  ngOnDestroy() {
    this.dashboardService.setFilterCategory("venue", "", "");
  }
}
